import { useState, useEffect, useRef, useMemo } from 'react';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { aspectRatiosMatch } from '../../../../../services/contents';
import { redistributePendingShows } from '../../services/showsRedistribution';

const useResolutionLogic = ({
  // redux state
  cart,
  listOfContentsInCampaign,
  customContents,
  toleranceResolution,
  listResolutionWithContent,
  editCampaignActive,
  currentCampaign,
  creationType,
  placeholders,
  isSuperUser,
  isPaymentStatusApproved,
  toleranceResolutionInit,
  initialBackup,
  hasInitialBackup,
  // redux actions
  setListResolutionWithContent,
  setListOfContentsInCampaign,
  updateCustomContents,
  applyRelationOnScreens,
  setToleranceResolution,
  setInitialBackup,
  setListOfPlaceholdersInCampaign,
  setListResolutionWithPlaceholders,
  getPlaceholders,
  restoreCampaignToOriginalState,
  clearCustomContents,
  cleanListOfContentsInCampaign,
  // component props
  groupedDisplays,
  filterByContent,
  selectedContent,
  fromModal,
  groupName,
  // from modal state
  deletingContentFromLibrary,
  setDeletingContentFromLibrary,
  reportSingleData
}) => {
  const { t } = useTranslation();

  // ── Local state ──────────────────────────────────────────────────
  let sliderRef = useRef(null);
  const prevListOfContents = useRef(listOfContentsInCampaign);
  const placeholdersInjected = useRef(false);
  const [currentStick, setCurrentStick] = useState(0);
  const [loadingListOfResolutions, setLoadingListOfResolutions] = useState(true);
  const [slidesToScroll, setSlidesToScroll] = useState(3);
  const [listResolution, setListResolution] = useState([]);
  const [hasDifferentResolution, setHasDifferentResolution] = useState(false);
  const [hasDifferentDurationSlot, setHasDifferentDurationSlot] = useState(false);
  const [isCreatedEditCampaign, setIsCreatedEditCampaign] = useState(false);
  const [resolutionFilterIndex, setResolutionFilterIndex] = useState(null);
  const [versionFilter, setVersionFilter] = useState(null);
  const [isLoaderView, setIsLoaderView] = useState(false);

  // ── Pure helpers ─────────────────────────────────────────────────

  const sortContentsByVersion = contents => {
    return contents.sort((a, b) => {
      const vA = parseInt((a.content_version_name || 'V1').substring(1));
      const vB = parseInt((b.content_version_name || 'V1').substring(1));
      return (isNaN(vA) ? 1 : vA) - (isNaN(vB) ? 1 : vB);
    });
  };

  const getClosestAspectRatio = (contentAddedInRelation, listResolutionItem, contentResolution) => {
    if (
      contentAddedInRelation.width === listResolutionItem.resolution_width &&
      contentAddedInRelation.height === listResolutionItem.resolution_height
    )
      return contentAddedInRelation;
    if (
      contentResolution.width === listResolutionItem.resolution_width &&
      contentResolution.height === listResolutionItem.resolution_height
    )
      return contentResolution;

    const arAdded = contentAddedInRelation.width / contentAddedInRelation.height;
    const arList = listResolutionItem.resolution_width / listResolutionItem.resolution_height;
    const arResolution = contentResolution.width / contentResolution.height;

    return Math.abs(arAdded - arList) < Math.abs(arResolution - arList)
      ? contentAddedInRelation
      : contentResolution;
  };

  const pushFormattedContent = (contentResolution, listResolutionItem, contentMatch) => {
    const [isMatch, difference] = aspectRatiosMatch(
      contentResolution.width,
      contentResolution.height,
      listResolutionItem.resolution_width,
      listResolutionItem.resolution_height,
      toleranceResolution
    );

    if (selectedContent && selectedContent.id !== contentResolution.id) return;
    if (!isMatch) return;

    contentMatch.push({
      ...contentResolution,
      id: contentResolution.id,
      file_thumb: contentResolution.file_thumb,
      name: contentResolution.name,
      content_version_name: contentResolution.content_version_name,
      differentResolution: difference ? isMatch : false,
      length: contentResolution.length
    });
  };

  const matchResolutionContent = (contentsInCampaign, listResolutionItem, contentAction, applyReplacement) => {
    let contentMatch = [];
    const listDisplay = listResolutionItem.displays.map(d => d.id);
    contentsInCampaign.forEach(contentResolution => {
      if (contentResolution.isPlaceholder &&
        (contentResolution.width !== listResolutionItem.resolution_width ||
        contentResolution.height !== listResolutionItem.resolution_height)
      ) return;
      if (
        contentAction &&
        contentResolution.id != contentAction.id &&
        !listResolutionItem.contents.some(c => c && c.id === contentResolution.id)
      )
        return;

      const contentAddedInRelation = contentMatch.find(
        r => r.content_version_name === contentResolution.content_version_name
      );

      if (contentAddedInRelation && contentAddedInRelation.id !== contentResolution.id) {
        if (contentAddedInRelation.isPlaceholder && !contentResolution.isPlaceholder) {
          const [isMatch] = aspectRatiosMatch(
            contentResolution.width,
            contentResolution.height,
            listResolutionItem.resolution_width,
            listResolutionItem.resolution_height,
            toleranceResolution
          );
          if (isMatch) {
            applyReplacement(contentAddedInRelation.id, listDisplay, contentResolution);
            contentMatch = contentMatch.filter(c => c.id !== contentAddedInRelation.id);
            pushFormattedContent(contentResolution, listResolutionItem, contentMatch);
            return;
          }
        }
        if (!contentAddedInRelation.isPlaceholder && contentResolution.isPlaceholder) {
          const [isMatch] = aspectRatiosMatch(
            contentResolution.width,
            contentResolution.height,
            listResolutionItem.resolution_width,
            listResolutionItem.resolution_height,
            toleranceResolution
          );
          if (isMatch) {
            applyReplacement(contentResolution.id, listDisplay, contentAddedInRelation);
          }
          return;
        }

        if (
          !(
            contentAddedInRelation.height === listResolutionItem.resolution_height &&
            contentAddedInRelation.width === listResolutionItem.resolution_width
          )
        ) {
          const closest = getClosestAspectRatio(
            contentAddedInRelation,
            listResolutionItem,
            contentResolution
          );
          if (closest.id !== contentAddedInRelation.id) {
            applyReplacement(contentAddedInRelation.id, listDisplay, contentResolution);
            contentMatch = contentMatch.filter(c => c.id !== contentAddedInRelation.id);
            pushFormattedContent(closest, listResolutionItem, contentMatch);
          }
        }
      }

      if (!contentAddedInRelation) {
        pushFormattedContent(contentResolution, listResolutionItem, contentMatch);
      }
    });

    return sortContentsByVersion(contentMatch);
  };

  const searchContentWhenUpdateCampaign = relation => {
    const displayIds = relation.displays.map(d => d.id);
    const listContents = [];

    customContents.forEach(cc => {
      if (!displayIds.includes(cc.displayId)) return;
      if (cc.content.isPlaceholder) {
        const versionName = cc.content.content_version_name || cc.content_version_name || 'V1';
        if (
          !listContents.find(
            c => c.id === cc.content.id && (c.content_version_name || 'V1') === versionName
          )
        ) {
          listContents.push({
            ...cc.content,
            content_version_name: versionName,
            differentResolution: false,
            isInactive: cc.active !== 1,
            isPendingContent: true
          });
        }
        return;
      }
      const versionName = cc.content.content_version_name || cc.content_version_name || 'V1';

      if (
        listContents.find(
          c => c.id === cc.content.id && (c.content_version_name || 'V1') === versionName
        )
      ) {
        return;
      }

      const [isMatch, difference] = aspectRatiosMatch(
        cc.content.width,
        cc.content.height,
        relation.resolution_width,
        relation.resolution_height,
        toleranceResolution
      );

      listContents.push({
        ...cc.content,
        content_version_name: versionName,
        differentResolution: !isMatch ? true : difference ? isMatch : false,
        isInactive: cc.active !== 1
      });
    });
    return sortContentsByVersion(listContents);
  };

  const createRelation = (uniqueResolutionsArray, actionWithRelation, newDisplays = null) => {
    const { displays, contents } = uniqueResolutionsArray;
    let relations = [];

    if (!customContents || customContents.length === 0 || actionWithRelation === 'cleanRelations') {
      displays.forEach(display => {
        contents.forEach(content => {
          relations.push({
            displayId: display.id,
            contentId: content.id,
            content_version_name: content.content_version_name
          });
        });
      });
    } else {
      displays.forEach(display => {
        if (groupedDisplays) {
          const groupedIds = groupedDisplays.map(d => d.id);
          if (!groupedIds.includes(display.id)) return;
        }

        const filteredContents = customContents.filter(c => c.displayId === display.id);
        filteredContents.forEach(cc => {
          relations.push({
            displayId: cc.displayId,
            contentId: cc.content.id,
            content_version_name: cc.content.content_version_name
          });
        });

        if (actionWithRelation === 'updateContent') {
          const newContents = listOfContentsInCampaign.filter(
            c =>
              !customContents.some(
                cc =>
                  cc.content.id === c.id &&
                  (cc.content.content_version_name || cc.content_version_name) ===
                    c.content_version_name
              )
          );

          if (groupedDisplays && newContents[0]) {
            if (filterByContent) {
              const customContentAux = customContents.map(cc => {
                if (cc.displayId === display.id && cc.content.id === filterByContent.id) {
                  return { ...cc, content: newContents[0], isPendingContent: false };
                }
                return cc;
              });

              updateCustomContents(customContentAux);

              relations = relations.map(r => {
                if (r.displayId === display.id && r.contentId === filterByContent.id) {
                  return {
                    displayId: display.id,
                    contentId: newContents[0].id,
                    content_version_name: newContents[0].content_version_name
                  };
                }
                return r;
              });
            }
          }
        }

        if (
          actionWithRelation === 'addDisplays' &&
          newDisplays &&
          newDisplays.find(item => item.id === display.id)
        ) {
          contents.forEach(content => {
            relations.push({
              displayId: display.id,
              contentId: content.id,
              content_version_name: content.content_version_name
            });
          });
        }

        if (actionWithRelation === 'checkAllContent') {
          contents.forEach(c =>
            relations.push({
              displayId: display.id,
              contentId: c.id,
              content_version_name: c.content_version_name
            })
          );
        }

        if (actionWithRelation === 'checkContent' || actionWithRelation === 'toleranceUpdate') {
          const listDisplay = uniqueResolutionsArray.displays.map(d => d.id);
          const newContents = contents.filter(
            c =>
              !uniqueResolutionsArray.contentsDisplays.some(
                cd =>
                  cd.contentId === c.id &&
                  (cd.content_version_name || 'V1') === (c.content_version_name || 'V1') &&
                  listDisplay.includes(cd.displayId)
              )
          );

          newContents.forEach(c =>
            relations.push({
              displayId: display.id,
              contentId: c.id,
              content_version_name: c.content_version_name
            })
          );
        }

        if (
          actionWithRelation === 'addDeletedContent' ||
          actionWithRelation === 'addDeletedContentFromModal' ||
          actionWithRelation === 'deletingContentFromLibrary'
        ) {
          const newContents = contents.filter(
            c =>
              !customContents.some(
                cc =>
                  cc.content.id === c.id &&
                  (cc.content.content_version_name || cc.content_version_name) ===
                    c.content_version_name
              )
          );
          newContents.forEach(c =>
            relations.push({
              displayId: display.id,
              contentId: c.id,
              content_version_name: c.content_version_name
            })
          );
        }
      });
    }

    const uniqueRelations = [];
    relations.forEach(r => {
      const exists = uniqueRelations.find(
        ur =>
          ur.displayId === r.displayId &&
          ur.contentId === r.contentId &&
          ur.content_version_name === r.content_version_name
      );
      if (!exists) uniqueRelations.push(r);
    });

    return uniqueRelations;
  };

  const isModifiedListContent = () => {
    const prevContents = prevListOfContents.current;
    prevListOfContents.current = listOfContentsInCampaign;
    const currentIds = listOfContentsInCampaign.map(c => c.id);
    const prevIds = prevContents.map(c => c.id);

    let contentAction = listOfContentsInCampaign.filter(c => !prevIds.includes(c.id));
    contentAction =
      contentAction.length > 0
        ? contentAction
        : prevContents.filter(c => !currentIds.includes(c.id));

    return contentAction.length > 0 ? contentAction[0] : null;
  };

  const loadViewWithContentsDisplays = (actionWithRelation, isAddFromModal) => {
    const isLoaderUpdateView =
      editCampaignActive && !isCreatedEditCampaign && actionWithRelation !== 'cleanRelations';
    if ((isLoaderUpdateView || isLoaderView) && !isAddFromModal) return true;
    return false;
  };

  const repairMissingRelations = (newRelation, workingCustomContents, templateByDisplayId) => {
    if (!newRelation.contentsDisplays) return workingCustomContents;

    let updatedCustomContents = workingCustomContents;
    newRelation.contentsDisplays.forEach(expectedRelation => {
      const relationAlreadyExists = updatedCustomContents.some(
        customContent =>
          customContent.displayId === expectedRelation.displayId &&
          customContent.content.id === expectedRelation.contentId
      );
      if (relationAlreadyExists) return;

      const contentToAssign = newRelation.contents.find(
        content => content.id === expectedRelation.contentId
      );
      if (!contentToAssign) return;

      const templateRelation = templateByDisplayId[expectedRelation.displayId];

      updatedCustomContents = [
        ...updatedCustomContents,
        {
          ...(templateRelation || {}),
          relationId: `temp_${expectedRelation.displayId}_${expectedRelation.contentId}_${updatedCustomContents.length}`,
          displayId: expectedRelation.displayId,
          active: 1,
          content: contentToAssign,
          isPendingContent: false
        }
      ];
    });

    return updatedCustomContents;
  };

  const handleRemovedDisplays = (newRelation, customContentsSnapshot) => {
    const removedDisplays = newRelation.contentsDisplays.filter(
      item =>
        !newRelation.contents.some(
          c =>
            c.id === item.contentId &&
            (c.content_version_name || 'V1') === (item.content_version_name || 'V1')
        )
    );

    if (removedDisplays.length === 0) {
      return { newRelation, customContents: customContentsSnapshot };
    }

    newRelation.contentsDisplays = newRelation.contentsDisplays.filter(item =>
      newRelation.contents.some(
        c =>
          c.id === item.contentId &&
          (c.content_version_name || 'V1') === (item.content_version_name || 'V1')
      )
    );

    const currentRelationDisplayIds = newRelation.displays.map(d => d.id);
    const updatedCustomContents = customContentsSnapshot.filter(cc => {
      if (!currentRelationDisplayIds.includes(cc.displayId)) {
        return true;
      }

      const isBeingRemoved = removedDisplays.some(
        r =>
          r.contentId === cc.content.id &&
          r.displayId === cc.displayId &&
          (r.content_version_name || 'V1') === (cc.content.content_version_name || 'V1')
      );

      return !isBeingRemoved;
    });

    return { newRelation, customContents: updatedCustomContents };
  };

  const addRelations = (uniqueResolutionsArray, actionWithRelation, newDisplays = null) => {
    let actionForCustomContents = '';
    const isAddFromModal = groupedDisplays && actionWithRelation === 'addDeletedContentFromModal';

    let workingCustomContents = customContents;
    let customContentsChanged = false;
    const applyReplacement = (contentId, listDisplay, contentToReplace) => {
      workingCustomContents = workingCustomContents.map(cc =>
        cc.content.id === contentId && (listDisplay.includes(cc.displayId) || listDisplay.length === 0)
          ? { ...cc, content: contentToReplace }
          : cc
      );
      customContentsChanged = true;
    };

    const uniqueResolutions = uniqueResolutionsArray.map(relation => {
      let newRelation = { 
        contents: [...relation.contents],
        contentsDisplays: [...relation.contentsDisplays],
        ...relation, 
      };
      let hasDisplay = true;
      let contentAction = null;

      if (actionWithRelation !== 'cleanRelations' && actionWithRelation !== 'toleranceUpdate') {
        contentAction = isModifiedListContent();
      }

      const addNewRelation =
        newDisplays &&
        newDisplays.find(
          d =>
            d.resolution_width === relation.resolution_width &&
            d.resolution_height === relation.resolution_height
        );

      if (editCampaignActive && actionWithRelation === 'toleranceUpdate') {
        newRelation.contents = searchContentWhenUpdateCampaign(relation);
        newRelation.contentsDisplays = createRelation(newRelation, 'toleranceUpdate');
      } else {
        if (
          (editCampaignActive &&
            !addNewRelation &&
            (!isCreatedEditCampaign || isPaymentStatusApproved) &&
            actionWithRelation !== 'cleanRelations') ||
          (groupedDisplays && !isAddFromModal)
        ) {
          newRelation.contents = searchContentWhenUpdateCampaign(relation);
        } else {
          newRelation.contents = matchResolutionContent(
            listOfContentsInCampaign,
            relation,
            contentAction,
            applyReplacement
          );
        }

        if (newRelation.contents && newRelation.contents.length === relation.contents.length) {
          actionForCustomContents = 'updatedCustomContent';
        }

        if (groupedDisplays) {
          const groupedIds = groupedDisplays.map(d => d.id);
          hasDisplay = relation.displays.some(d => groupedIds.includes(d.id));
        }

        if (newRelation.contents.length > 0 && hasDisplay) {
          const actionForCreateRelation =
            loadViewWithContentsDisplays(actionWithRelation, isAddFromModal) && !newDisplays
              ? 'isLoaderUpdateView'
              : actionWithRelation;

          newRelation.contentsDisplays = createRelation(
            newRelation,
            actionForCreateRelation,
            newDisplays
          );
        }
      }

      if (
        editCampaignActive &&
        (actionWithRelation === 'checkAllContent' ||
          actionWithRelation === 'checkContent' ||
          actionWithRelation === 'toleranceUpdate' ||
          actionWithRelation === 'addDeletedContent' ||
          actionWithRelation === 'addDeletedContentFromModal' ||
          actionWithRelation === 'deletingContentFromLibrary')
      ) {
        const templateByDisplayId = {};
        workingCustomContents.forEach(customContent => {
          if (!templateByDisplayId[customContent.displayId]) {
            templateByDisplayId[customContent.displayId] = customContent;
          }
        });

        const removalResult = handleRemovedDisplays(newRelation, workingCustomContents);
        newRelation = removalResult.newRelation;
        if (removalResult.customContents !== workingCustomContents) {
          workingCustomContents = removalResult.customContents;
          customContentsChanged = true;
        }

        const repairedCustomContents = repairMissingRelations(
          newRelation,
          workingCustomContents,
          templateByDisplayId
        );
        if (repairedCustomContents !== workingCustomContents) {
          workingCustomContents = repairedCustomContents;
          customContentsChanged = true;
        }
      }

      return newRelation;
    });

    if (customContentsChanged) {
      updateCustomContents(workingCustomContents);

      const orphanedPlaceholderIds = new Set(
        listOfContentsInCampaign
          .filter(
            content =>
              content.isPlaceholder &&
              !workingCustomContents.some(customContent => customContent.content.id === content.id)
          )
          .map(content => content.id)
      );
      if (orphanedPlaceholderIds.size > 0) {
        setListOfContentsInCampaign(
          listOfContentsInCampaign.filter(content => !orphanedPlaceholderIds.has(content.id))
        );
      }
    }

    return [uniqueResolutions, actionForCustomContents];
  };

  const getListOfResolutions = (customCart, newDisplays = null) => {
    const uniqueResolutions = {};

    customCart.forEach(item => {
      const key = `${item.resolution_height}-${item.resolution_width}`;

      const displayItem = {
        id: item.id,
        fill_screen: item.fill_screen,
        smart_campaign_cpm: item.smart_campaign_cpm,
        slot_length: item.slot_length,
        format_settings: item.format_settings
      };

      if (!uniqueResolutions[key]) {
        uniqueResolutions[key] = {
          resolution_height: item.resolution_height,
          resolution_width: item.resolution_width,
          countScreen: 1,
          contents: [],
          displays: [displayItem],
          contentsDisplays: []
        };
      } else {
        uniqueResolutions[key].displays.push(displayItem);
        uniqueResolutions[key].countScreen++;
      }
    });

    let uniqueResolutionsArray = Object.values(uniqueResolutions);
    const typeAux = newDisplays ? 'addDisplays' : 'addDeletedContent';

    uniqueResolutionsArray = addRelations(uniqueResolutionsArray, typeAux, newDisplays)[0];
    return uniqueResolutionsArray;
  };

  const filteredDisplays = () => {
    const groupedDisplayIds = groupedDisplays.map(d => d.id);
    const customCart = cart.filter(d => groupedDisplayIds.includes(d.id));
    return getListOfResolutions(deletingContentFromLibrary ? cart : customCart);
  };

  const updateListResolutions = type => {
    if (loadingListOfResolutions || listResolutionWithContent.length === 0) return;

    let updatedListResolution = listResolutionWithContent;
    let removedRelations = [];

    if (groupedDisplays) {
      const displaysId = groupedDisplays.map(d => d.id);
      updatedListResolution = updatedListResolution.filter(r =>
        r.displays.some(d => displaysId.includes(d.id))
      );
      removedRelations = listResolutionWithContent.filter(
        r => !r.displays.some(d => displaysId.includes(d.id))
      );
    }

    updatedListResolution = addRelations(updatedListResolution, type);

    if (!groupedDisplays) {
      setListResolutionWithContent(updatedListResolution[0]);
    }

    if (groupedDisplays) {
      const hasDiffRes = updatedListResolution[0].some(item =>
        item.contents.some(c => c.differentResolution === true)
      );
      setHasDifferentResolution(hasDiffRes);

      setListResolutionWithContent([...updatedListResolution[0], ...removedRelations]);
      setListResolution(updatedListResolution[0]);
    }

    if (
      groupedDisplays &&
      (type === 'deletingContentFromLibrary' || updatedListResolution[1] !== 'updatedCustomContent')
    ) {
      applyRelationOnScreens(cart, updatedListResolution[0]);
    }
  };

  const updateListResolutionsForEditingTolerance = () => {
    if (loadingListOfResolutions || listResolutionWithContent.length === 0) return;

    let resolutionsToProcess = listResolutionWithContent;
    let removedRelations = [];

    if (groupedDisplays) {
      const displaysId = groupedDisplays.map(d => d.id);
      resolutionsToProcess = resolutionsToProcess.filter(r =>
        r.displays.some(d => displaysId.includes(d.id))
      );
      removedRelations = listResolutionWithContent.filter(
        r => !r.displays.some(d => displaysId.includes(d.id))
      );
    }

    let accumulatedCustomContents = [...customContents];

    const updatedResolutions = resolutionsToProcess.map(relation => {
      const relationDisplayIds = relation.displays.map(d => d.id);

      accumulatedCustomContents = accumulatedCustomContents.filter(cc => {
        if (!relationDisplayIds.includes(cc.displayId) || cc.active !== 1) return true;
        if (cc.content.isPlaceholder) return true;

        const [isMatch] = aspectRatiosMatch(
          cc.content.width,
          cc.content.height,
          relation.resolution_width,
          relation.resolution_height,
          toleranceResolution
        );
        return isMatch;
      });

      const contentsDisplays = [];
      const contentsUnion = [];
      let fallbackPlaceholder = null;

      const displaysToProcess = groupedDisplays
        ? relation.displays.filter(d => groupedDisplays.some(gd => gd.id === d.id))
        : relation.displays;

      displaysToProcess.forEach(display => {
        const existingForDisplay = accumulatedCustomContents.filter(
          cc => cc.displayId === display.id && cc.active === 1
        );
        const realExisting = existingForDisplay.filter(cc => !cc.content.isPlaceholder);
        const placeholderExisting = existingForDisplay.filter(cc => cc.content.isPlaceholder);

        const placeholderSpots = placeholderExisting.reduce(
          (sum, cc) => sum + (cc.promised_shows || 0),
          0
        );
        const realSpots = realExisting.reduce((sum, cc) => sum + (cc.promised_shows || 0), 0);
        const totalSpots = placeholderSpots + realSpots;

        realExisting.forEach(cc => {
          const vn = cc.content.content_version_name || 'V1';

          contentsDisplays.push({
            displayId: display.id,
            contentId: cc.content.id,
            content_version_name: vn
          });

          if (!contentsUnion.some(c => c.id === cc.content.id && c.content_version_name === vn)) {
            contentsUnion.push({
              ...cc.content,
              content_version_name: vn,
              differentResolution: false
            });
          }
        });

        const existingVersionNums = realExisting
          .map(cc => parseInt((cc.content.content_version_name || 'V1').substring(1)))
          .filter(n => !isNaN(n));

        const candidateNewContents = [...listOfContentsInCampaign]
          .filter(c => !c.isPlaceholder && !realExisting.some(cc => cc.content.id === c.id))
          .sort((a, b) => {
            const vA = parseInt((a.content_version_name || 'V1').substring(1));
            const vB = parseInt((b.content_version_name || 'V1').substring(1));
            return vA - vB;
          });

        candidateNewContents.forEach(newContent => {
          const [isMatch, difference] = aspectRatiosMatch(
            newContent.width,
            newContent.height,
            relation.resolution_width,
            relation.resolution_height,
            toleranceResolution
          );

          if (!isMatch) return;

          const versionNum = parseInt((newContent.content_version_name || 'V1').substring(1));
          if (existingVersionNums.includes(versionNum)) return;
          for (let v = 1; v < versionNum; v++) {
            if (!existingVersionNums.includes(v)) return;
          }
          existingVersionNums.push(versionNum);

          const vn = newContent.content_version_name || 'V1';

          contentsDisplays.push({
            displayId: display.id,
            contentId: newContent.id,
            content_version_name: vn
          });

          if (!contentsUnion.some(c => c.id === newContent.id && c.content_version_name === vn)) {
            contentsUnion.push({
              ...newContent,
              differentResolution: difference ? isMatch : false
            });
          }

          const templateCc =
            realExisting[0] ||
            (initialBackup &&
              initialBackup.customContents &&
              initialBackup.customContents.find(cc => cc.displayId === display.id));

          const newRelationObj = {
            rules: [],
            ...(templateCc || {}),
            relationId: `${display.id}_temp_${Date.now()}_${Math.floor(Math.random() * 99999999)}`,
            displayId: display.id,
            active: 1,
            content: { ...newContent },
            revision_status: 'pending',
            reject_reason: null,
            isReplacementAction: false,
            parentRelationId: null,
            promised_shows: 0,
            promised_shows_subsidized: 0

          };

          // solo edición de propuesta
          const isEditProposal =
            editCampaignActive && currentCampaign && currentCampaign.status === 'proposal';

          if (isEditProposal) {
            if (newRelationObj.promised_shows === undefined) newRelationObj.promised_shows = 0;
            if (newRelationObj.relationId === undefined)
              newRelationObj.relationId = `temp_tol_${Date.now()}_${display.id}_${vn}`;
            if (newRelationObj.subsidized_price === undefined)
              newRelationObj.subsidized_price = false;
            if (newRelationObj.promised_shows_subsidized === undefined)
              newRelationObj.promised_shows_subsidized = 0;
          }

          if (editCampaignActive && newRelationObj.relationId === undefined)
            newRelationObj.relationId = `${display.id}_temp_${Date.now()}`;

          accumulatedCustomContents.push(newRelationObj);
          realExisting.push({ displayId: display.id, active: 1, content: newContent });
        });

        if (realExisting.length > 0) {
          if (placeholderExisting.length > 0) {
            accumulatedCustomContents = accumulatedCustomContents.filter(
              cc => !(cc.displayId === display.id && cc.active === 1 && cc.content.isPlaceholder)
            );
          }

          // Resolve realExisting to its full records in accumulatedCustomContents
          // (some entries pushed in this loop are partial: { displayId, active, content }).
          const realExistingFull = realExisting
            .map(rel =>
              accumulatedCustomContents.find(
                cc =>
                  cc.displayId === display.id &&
                  cc.active === 1 &&
                  cc.content.id === rel.content.id &&
                  (cc.content.content_version_name || 'V1') ===
                    (rel.content.content_version_name || 'V1')
              )
            )
            .filter(Boolean);

          if (realExistingFull.length > 0) {
            const placeholderSubsidizedShows = placeholderExisting.reduce(
              (sum, cc) => sum + (cc.promised_shows_subsidized || 0),
              0
            );

            // Placeholders had no consumed, so their full promised_shows joins the pool.
            const { updates } = redistributePendingShows({
              relations: realExistingFull,
              reportSingleData,
              displayId: display.id,
              extraPaidPool: placeholderSpots,
              extraSubsidizedPool: placeholderSubsidizedShows,
            });

            realExistingFull.forEach((rel, i) => {
              const idx = accumulatedCustomContents.indexOf(rel);
              if (idx !== -1) {
                accumulatedCustomContents[idx] = { ...accumulatedCustomContents[idx], ...updates[i] };
              }
            });
          }
        } else if (placeholderExisting.length > 0) {
          placeholderExisting.forEach(cc => {
            const vn = cc.content.content_version_name || 'V1';
            contentsDisplays.push({
              displayId: display.id,
              contentId: cc.content.id,
              content_version_name: vn
            });
            if (!fallbackPlaceholder) {
              fallbackPlaceholder = {
                ...cc.content,
                content_version_name: vn,
                differentResolution: false
              };
            }
          });
        }
      });

      if (contentsUnion.length === 0 && fallbackPlaceholder) {
        contentsUnion.push(fallbackPlaceholder);
      }

      if (contentsUnion.length === 0) {
        const alreadyInjected = relation.contents.find(c => c.isPlaceholder);
        if (alreadyInjected) {
          contentsUnion.push(alreadyInjected);
        }
      }

      if (contentsUnion.length > 0) {
        const defaultContent = contentsUnion[0];
        displaysToProcess.forEach(display => {
          const hasLink = contentsDisplays.some(content => content.displayId === display.id);
          if (!hasLink) {
            contentsDisplays.push({
              displayId: display.id,
              contentId: defaultContent.id,
              content_version_name: defaultContent.content_version_name || 'V1'
            });
          }
        });
      }

      sortContentsByVersion(contentsUnion);

      return { ...relation, contents: contentsUnion, contentsDisplays };
    });

    updateCustomContents(accumulatedCustomContents);

    if (!groupedDisplays) {
      setListResolutionWithContent(updatedResolutions);
    } else {
      const hasDiffRes = updatedResolutions.some(item =>
        item.contents.some(c => c.differentResolution === true)
      );
      setHasDifferentResolution(hasDiffRes);

      setListResolutionWithContent([...updatedResolutions, ...removedRelations]);
      setListResolution(updatedResolutions);

      applyRelationOnScreens(cart, updatedResolutions);
    }
  };

  const defineListResolution = lrwc => {
    const listResolutionAux = groupedDisplays ? filteredDisplays() : lrwc;
    const hasDiffRes = listResolutionAux.some(item =>
      item.contents.some(c => c.differentResolution === true)
    );

    setListResolution(listResolutionAux);
    setHasDifferentResolution(hasDiffRes);
  };

  const setTolerance = value => {
    const val = parseInt(value);
    const maxSuperUser = 50;
    const maxUser = 25;

    if (isNaN(val) || val < 0) {
      if (editCampaignActive && isPaymentStatusApproved && toleranceResolutionInit > 0) {
        return setToleranceResolution(toleranceResolutionInit);
      }
      return setToleranceResolution(0);
    }

    if (editCampaignActive && isPaymentStatusApproved && val < toleranceResolutionInit) {
      return setToleranceResolution(toleranceResolutionInit);
    }

    if (val > maxUser && !isSuperUser) return setToleranceResolution(maxUser);
    if (val > maxSuperUser && isSuperUser) return setToleranceResolution(maxSuperUser);

    setToleranceResolution(val);
  };

  const calculateSlidesToShow = () => {
    if (toleranceResolution >= 25) return setSlidesToScroll(1);
    if (toleranceResolution >= 15) return setSlidesToScroll(2);
    return setSlidesToScroll(3);
  };

  const initSlider = () => {
    if (!sliderRef.current) return;
    sliderRef.current.slickGoTo(0);
  };

  const isTheLastOfTheRelationship = (relation, content) => {
    const listDisplaysWithContent = relation.contentsDisplays.filter(
      item => item.contentId === content.id
    );
    for (const display of listDisplaysWithContent) {
      if (
        relation.contentsDisplays.filter(item => item.displayId === display.displayId).length === 1
      )
        return true;
    }
    return false;
  };

  const listDisplay = groupedDisplays ? groupedDisplays.map(d => d.id) : null;
  const haveContentInRelation = content => {
    if (!listDisplay) return false;
    return customContents.some(
      cc => cc.content.id === content.id && listDisplay.includes(cc.displayId)
    );
  };

  const checkAddInRelation = content => {
    let listResolutionWithContentAux = listResolutionWithContent;

    let workingCustomContents = customContents;
    let customContentsChanged = false;
    const applyReplacement = (contentId, listDisplay, contentToReplace) => {
      workingCustomContents = workingCustomContents.map(cc =>
        cc.content.id === contentId && (listDisplay.includes(cc.displayId) || listDisplay.length === 0)
          ? { ...cc, content: contentToReplace }
          : cc
      );
      customContentsChanged = true;
    };

    const uniqueResolutions = listResolution.map(relation => {
      let newRelation = {
        ...relation,
        contents: [...relation.contents],
        contentsDisplays: [...relation.contentsDisplays]
      };
      newRelation.contents = matchResolutionContent(
        listOfContentsInCampaign,
        relation,
        content,
        applyReplacement
      );

      const groupedIds = groupedDisplays.map(d => d.id);
      const hasDisplay = relation.displays.some(d => groupedIds.includes(d.id));

      if (
        newRelation.contents.length > 0 &&
        hasDisplay &&
        newRelation.contents.some(item => item.id === content.id)
      ) {
        newRelation.contentsDisplays = createRelation(newRelation, 'checkContent');
      }

      const templateByDisplayId = {};
      workingCustomContents.forEach(customContent => {
        if (!templateByDisplayId[customContent.displayId]) {
          templateByDisplayId[customContent.displayId] = customContent;
        }
      });

      const removalResult = handleRemovedDisplays(newRelation, workingCustomContents);
      newRelation = removalResult.newRelation;
      if (removalResult.customContents !== workingCustomContents) {
        workingCustomContents = removalResult.customContents;
        customContentsChanged = true;
      }

      const repairedCustomContents = repairMissingRelations(
        newRelation,
        workingCustomContents,
        templateByDisplayId
      );
      if (repairedCustomContents !== workingCustomContents) {
        workingCustomContents = repairedCustomContents;
        customContentsChanged = true;
      }

      listResolutionWithContentAux = listResolutionWithContentAux.map(item =>
        item.resolution_height === newRelation.resolution_height &&
        item.resolution_width === newRelation.resolution_width
          ? newRelation
          : item
      );
      return newRelation;
    });

    if (customContentsChanged) {
      updateCustomContents(workingCustomContents);

      const orphanedPlaceholderIds = new Set(
        listOfContentsInCampaign
          .filter(
            content =>
              content.isPlaceholder &&
              !workingCustomContents.some(customContent => customContent.content.id === content.id)
          )
          .map(content => content.id)
      );
      if (orphanedPlaceholderIds.size > 0) {
        setListOfContentsInCampaign(
          listOfContentsInCampaign.filter(content => !orphanedPlaceholderIds.has(content.id))
        );
      }
    }

    setListResolution(uniqueResolutions);
    setListResolutionWithContent(listResolutionWithContentAux);
    if (typeof applyRelationOnScreens === 'function') {
      applyRelationOnScreens(groupedDisplays, listResolutionWithContentAux);
    }
  };

  const getTooltipAndCompatibility = content => {
    const isOnlyone = groupedDisplays && groupedDisplays.length === 1;

    const hasCompatibleResolution =
      listResolution &&
      listResolution.some(relation => {
        const [isMatch, difference] = aspectRatiosMatch(
          content.width,
          content.height,
          relation.resolution_width,
          relation.resolution_height,
          toleranceResolution
        );
        return isMatch && difference <= toleranceResolution;
      });

    const hasVersion =
      listResolution &&
      listResolution.some(relation =>
        relation.contents.some(cr => cr.content_version_name === content.content_version_name)
      );

    const tooltipText = hasVersion
      ? t('Version already exists for this resolution')
      : hasCompatibleResolution
      ? t('Apply ad to available resolutions')
      : t('Does not match any resolution, increase tolerance in the previous step.');

    const translationKey = isOnlyone ? 'resolution.mismatch.single' : 'resolution.mismatch.group';

    const message = t(translationKey, { tolerance: toleranceResolution });

    return { hasCompatibleResolution, message, tooltipText, hasVersion };
  };

  const openNotification = hasVersion => {
    notification.open({
      description: hasVersion
        ? t('Version already exists for this resolution')
        : t(
            "This ad doesn't match any of the resolutions. Increase the campaign tolerance in the previous step and try again."
          ),
      duration: 3
    });
  };

  const resolutionSubtitle = useMemo(() => {
    const isOnlyone = groupedDisplays && fromModal === 'display';
    const partOne = isOnlyone ? t('for the screen') : t('for screens grouped as');
    const partTwo = isOnlyone ? groupedDisplays[0] && groupedDisplays[0].name : groupName;
    return `${partOne} ${partTwo}`;
  }, [groupedDisplays, fromModal, groupName]);

  const resolutionTitle = useMemo(() => {
    const isOnlyone = groupedDisplays && groupedDisplays.length === 1;
    const hasDifferentVersion =
      listResolution &&
      listResolution[0] &&
      listResolution[0].contents.some(c => c.content_version_name !== 'V1');

    const totalCount = listResolution ? listResolution.length : 0;
    const assignedCount = listResolution
      ? listResolution.filter(r => r.contents && r.contents.length > 0).length
      : 0;

    const partOne = isOnlyone ? t('Required resolution') : t('Required resolutions');
    const counter = totalCount > 0 ? ` (${assignedCount}/${totalCount})` : '';
    const partTwo =
      hasDifferentVersion && isOnlyone && !filterByContent ? ` ${t('for these versions')}` : '';

    return `${partOne}${counter}${partTwo}`;
  }, [groupedDisplays, listResolution]);

  const listContentResolutions = useMemo(() => {
    const unique = listOfContentsInCampaign.reduce((acc, item) => {
      const exists = acc.some(r => r.width === item.width && r.height === item.height);
      if (!exists) acc.push({ width: item.width, height: item.height });
      return acc;
    }, []);

    setResolutionFilterIndex(null);
    return [...unique].sort((a, b) => a.width - b.width);
  }, [listOfContentsInCampaign]);

  const listOfContentsInCampaignFiltered = useMemo(() => {
    const resolutionFilter = listContentResolutions[resolutionFilterIndex];

    const filtered = listOfContentsInCampaign.filter(content => {
      if (content.isPlaceholder) return false;

      const matchesResolution = resolutionFilter
        ? content.width === resolutionFilter.width && content.height === resolutionFilter.height
        : true;
      const matchesVersion = versionFilter ? content.content_version_name === versionFilter : true;
      return matchesResolution && matchesVersion;
    });

    return [...filtered].sort((a, b) => {
      const vA = a.content_version_name ? parseInt(a.content_version_name.replace('V', '')) : 0;
      const vB = b.content_version_name ? parseInt(b.content_version_name.replace('V', '')) : 0;
      return vA - vB;
    });
  }, [listOfContentsInCampaign, resolutionFilterIndex, versionFilter]);

  const applyTolerance = () => {
    const backupContents = (initialBackup && initialBackup.listOfContentsInCampaign) || [];

    const sessionContents = listOfContentsInCampaign.filter(
      c =>
        !c.isPlaceholder &&
        !backupContents.some(
          bc => bc.id === c.id && bc.content_version_name === c.content_version_name
        )
    );

    let newCustomContents = [...customContents];
    let listResolutionWithContentAux = [...listResolutionWithContent];

    let tempIdCounter = 0;
    const makeTempId = suffix => `temp_${Date.now()}_${++tempIdCounter}_${suffix}`;

    const newVersionEntries = [];

    const updatedResolutions = listResolution.map(relation => {
      let newRelation = {
        ...relation,
        contents: [...relation.contents],
        contentsDisplays: [...relation.contentsDisplays]
      };

      const groupedIds = groupedDisplays ? groupedDisplays.map(d => d.id) : null;
      const hasDisplay = groupedIds ? relation.displays.some(d => groupedIds.includes(d.id)) : true;

      if (!hasDisplay) return newRelation;

      const displaysToProcess = groupedIds
        ? relation.displays.filter(d => groupedIds.includes(d.id))
        : relation.displays;

      sessionContents.forEach(sessionContent => {
        const [isMatch, difference] = aspectRatiosMatch(
          sessionContent.width,
          sessionContent.height,
          relation.resolution_width,
          relation.resolution_height,
          toleranceResolution
        );

        if (!isMatch) {
          displaysToProcess.forEach(display => {
            const idx = newCustomContents.findIndex(
              cc =>
                cc.displayId === display.id &&
                cc.content.id === sessionContent.id &&
                cc.active === 1 &&
                String(cc.relationId || '').startsWith('temp_')
            );
            if (idx !== -1) {
              const tempRel = newCustomContents[idx];
              const orphanedShows = tempRel.promised_shows || 0;

              if (tempRel.parentRelationId) {
                const parentIdx = newCustomContents.findIndex(
                  cc => cc.relationId === tempRel.parentRelationId && cc.active === 0
                );

                if (parentIdx !== -1) {
                  newCustomContents[parentIdx] = {
                    ...newCustomContents[parentIdx],
                    active: 1,
                    revision_status:
                      newCustomContents[parentIdx]._originalRevisionStatus ||
                      newCustomContents[parentIdx].revision_status,
                    _originalRevisionStatus: undefined
                  };
                }
              }

              newCustomContents.splice(idx, 1);

              if (orphanedShows > 0) {
                const remainingActive = newCustomContents.filter(
                  cc => cc.displayId === display.id && cc.active === 1
                );

                if (remainingActive.length > 0) {
                  const perRelation = Math.floor(orphanedShows / remainingActive.length);
                  let remainder = orphanedShows - perRelation * remainingActive.length;

                  remainingActive.forEach(rel => {
                    const relIdx = newCustomContents.findIndex(
                      c => c.relationId === rel.relationId
                    );
                    if (relIdx !== -1) {
                      const extra = remainder > 0 ? 1 : 0;
                      if (extra) remainder--;
                      newCustomContents[relIdx] = {
                        ...newCustomContents[relIdx],
                        promised_shows:
                          (newCustomContents[relIdx].promised_shows || 0) + perRelation + extra
                      };
                    }
                  });
                }
              }
            }
          });
          newRelation.contents = newRelation.contents.filter(c => c.id !== sessionContent.id);
          return;
        }

        const targetVersion = sessionContent.content_version_name;

        displaysToProcess.forEach(display => {
          const alreadyAdded = newCustomContents.some(
            cc =>
              cc.displayId === display.id && cc.content.id === sessionContent.id && cc.active === 1
          );
          if (alreadyAdded) return;

          const activeDisplayRelations = newCustomContents.filter(
            cc => cc.displayId === display.id && cc.active === 1
          );

          const existingTargetRelation = activeDisplayRelations.find(
            cc => cc.content.content_version_name === targetVersion
          );

          if (existingTargetRelation) {
            return;
          }

          const templateRelation = activeDisplayRelations[0];

          const newEntry = templateRelation
            ? {
                ...templateRelation,
                relationId: makeTempId(display.id),
                parentRelationId: null,
                isReplacementAction: false,
                displayId: display.id,
                active: 1,
                promised_shows: 0,
                content: { ...sessionContent, content_version_name: targetVersion },
                revision_status: 'pending',
                reject_reason: null
              }
            : {
                displayId: display.id,
                active: 1,
                promised_shows: 0,
                content: { ...sessionContent, content_version_name: targetVersion },
                revision_status: 'pending',
                reject_reason: null,
                isReplacementAction: false,
                relationId: makeTempId(display.id)
              };

          newCustomContents.push(newEntry);
          newVersionEntries.push({
            ...sessionContent,
            content_version_name: targetVersion
          });

          if (
            !newRelation.contents.some(
              c => c.id === sessionContent.id && c.content_version_name === targetVersion
            )
          ) {
            newRelation.contents.push({
              ...sessionContent,
              content_version_name: targetVersion,
              differentResolution: difference ? isMatch : false
            });
          }
        });
      });

      if (newRelation.contents.length > 0) {
        newRelation.contentsDisplays = createRelation(newRelation, 'checkAllContent');
      }

      sortContentsByVersion(newRelation.contents);

      listResolutionWithContentAux = listResolutionWithContentAux.map(item =>
        item.resolution_height === newRelation.resolution_height &&
        item.resolution_width === newRelation.resolution_width
          ? newRelation
          : item
      );

      return newRelation;
    });

    const uniqueNewVersionEntries = newVersionEntries.filter(
      (v, i, arr) =>
        arr.findIndex(a => a.id === v.id && a.content_version_name === v.content_version_name) ===
          i &&
        !listOfContentsInCampaign.some(
          c => c.id === v.id && c.content_version_name === v.content_version_name
        )
    );

    if (uniqueNewVersionEntries.length > 0) {
      setListOfContentsInCampaign([...listOfContentsInCampaign, ...uniqueNewVersionEntries]);
    }

    const additionDisplayIds = [
      ...new Set(
        newCustomContents
          .filter(
            cc =>
              String(cc.relationId || '').startsWith('temp_') &&
              !cc.isReplacementAction &&
              cc.active === 1
          )
          .map(cc => cc.displayId)
      )
    ];

    additionDisplayIds.forEach(displayId => {
      const activeRelations = newCustomContents.filter(
        cc => cc.displayId === displayId && cc.active === 1
      );
      if (activeRelations.length <= 1) return;

      const { updates } = redistributePendingShows({
        relations: activeRelations,
        reportSingleData,
        displayId,
      });

      activeRelations.forEach((rel, i) => {
        const idx = newCustomContents.indexOf(rel);
        if (idx !== -1) newCustomContents[idx] = { ...rel, ...updates[i] };
      });
    });

    const allRealContents = listOfContentsInCampaign.filter(c => !c.isPlaceholder);
    const allDisplayIds = groupedDisplays
      ? groupedDisplays.map(d => d.id)
      : [...new Set((cart || []).map(d => d.id))];

    allDisplayIds.forEach(displayId => {
      const display = (cart || []).find(d => d.id === displayId);
      if (!display) return;

      const filledIdx = newCustomContents.findIndex(
        cc => cc.displayId === displayId && cc.active === 1 && cc.isFilledFromPlaceholder === true
      );
      if (filledIdx === -1) return;

      const filledRel = newCustomContents[filledIdx];
      const screenWidth = display.resolution_width || display.display_resolution_width;
      const screenHeight = display.resolution_height || display.display_resolution_height;

      const [stillMatches] = aspectRatiosMatch(
        filledRel.content.width,
        filledRel.content.height,
        screenWidth,
        screenHeight,
        toleranceResolution
      );

      if (stillMatches) return;

      const originalPlaceholderContent = filledRel._originalPlaceholderContent;
      newCustomContents[filledIdx] = {
        ...filledRel,
        content: { ...originalPlaceholderContent },
        revision_status: 'pending_content',
        isPendingContent: true,
        isFilledFromPlaceholder: false,
        _originalPlaceholderContent: undefined,
        reject_reason: null
      };

      const revertedContentId = filledRel.content.id;
      const revertedVn = filledRel.content.content_version_name || 'V1';
      const originalVn = originalPlaceholderContent.content_version_name || 'V1';
      listResolutionWithContentAux = listResolutionWithContentAux.map(resolution => {
        const hasThisDisplay = resolution.displays.some(d => d.id === displayId);
        if (!hasThisDisplay) return resolution;

        const newContentsDisplays = resolution.contentsDisplays
          .filter(
            cd =>
              !(
                cd.displayId === displayId &&
                cd.contentId === revertedContentId &&
                cd.content_version_name === revertedVn
              )
          )
          .concat([
            {
              displayId,
              contentId: originalPlaceholderContent.id,
              content_version_name: originalVn
            }
          ]);

        const otherDisplaysWithContent = resolution.contentsDisplays.some(
          cd => cd.contentId === revertedContentId && cd.displayId !== displayId
        );
        const newContents = otherDisplaysWithContent
          ? resolution.contents
          : resolution.contents.filter(
              c => !(c.id === revertedContentId && c.content_version_name === revertedVn)
            );

        if (!newContents.some(c => c.id === originalPlaceholderContent.id)) {
          newContents.push({ ...originalPlaceholderContent, differentResolution: false });
        }

        sortContentsByVersion(newContents);

        return { ...resolution, contents: newContents, contentsDisplays: newContentsDisplays };
      });
    });

    allDisplayIds.forEach(displayId => {
      const display = (cart || []).find(d => d.id === displayId);
      if (!display) return;

      const activeForDisplay = newCustomContents.filter(
        cc => cc.displayId === displayId && cc.active === 1
      );
      const hasRealRelation = activeForDisplay.some(cc => !cc.isPendingContent);
      const placeholderIdx = newCustomContents.findIndex(
        cc => cc.displayId === displayId && cc.active === 1 && cc.isPendingContent === true
      );
      if (placeholderIdx === -1 || hasRealRelation) return;

      const screenWidth = display.resolution_width || display.display_resolution_width;
      const screenHeight = display.resolution_height || display.display_resolution_height;

      const matchingContent = allRealContents.find(content => {
        const [isMatch] = aspectRatiosMatch(
          content.width,
          content.height,
          screenWidth,
          screenHeight,
          toleranceResolution
        );
        return isMatch;
      });

      if (!matchingContent) return;

      const placeholderRel = newCustomContents[placeholderIdx];
      newCustomContents[placeholderIdx] = {
        ...placeholderRel,
        content: { ...matchingContent },
        revision_status: 'pending',
        isPendingContent: false,
        isFilledFromPlaceholder: true,
        _originalPlaceholderContent: { ...placeholderRel.content },
        reject_reason: null
      };

      const vn = matchingContent.content_version_name || 'V1';
      listResolutionWithContentAux = listResolutionWithContentAux.map(resolution => {
        const hasThisDisplay = resolution.displays.some(d => d.id === displayId);
        if (!hasThisDisplay) return resolution;

        const newContentsDisplays = resolution.contentsDisplays
          .filter(cd => !(cd.displayId === displayId && cd.contentId === placeholderRel.content.id))
          .concat([{ displayId, contentId: matchingContent.id, content_version_name: vn }]);

        const newContents = resolution.contents.filter(c => c.id !== placeholderRel.content.id);
        if (!newContents.some(c => c.id === matchingContent.id && c.content_version_name === vn)) {
          newContents.push({ ...matchingContent, differentResolution: true });
        }

        sortContentsByVersion(newContents);

        return { ...resolution, contents: newContents, contentsDisplays: newContentsDisplays };
      });
    });

    updateCustomContents(newCustomContents);
    setListResolution(updatedResolutions);
    setListResolutionWithContent(listResolutionWithContentAux);

    if (typeof applyRelationOnScreens === 'function') {
      applyRelationOnScreens(groupedDisplays, listResolutionWithContentAux);
    }
  };

  // ── Effects ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!groupedDisplays) {
      let newDisplays = null;
      if (listResolutionWithContent.length > 0) {
        newDisplays = cart.filter(
          d => !listResolutionWithContent.some(r => r.displays.some(item => item.id === d.id))
        );
      }
      setListResolutionWithContent(getListOfResolutions(cart, newDisplays));
    }
    setLoadingListOfResolutions(false);
    setListResolution([]);
  }, []);

  useEffect(() => {
    const navEntries = performance.getEntriesByType('navigation')[0];
    if (navEntries && navEntries.type === 'reload') setIsLoaderView(true);
  }, []);

  useEffect(() => {
    initSlider();
    calculateSlidesToShow();

    if (editCampaignActive && isPaymentStatusApproved) return;

    if (editCampaignActive && !isPaymentStatusApproved) updateListResolutionsForEditingTolerance();
    else updateListResolutions('cleanRelations');
  }, [toleranceResolution]);

  useEffect(() => {
    if (!deletingContentFromLibrary) {
      if (groupedDisplays && filterByContent) {
        updateListResolutions('updateContent');
      } else {
        let type = 'addDeletedContent';
        if (groupedDisplays) type = 'addDeletedContentFromModal';

        const isPendingPayment = currentCampaign && currentCampaign.payment_status === 'pending';

        if (editCampaignActive && (currentCampaign.status === 'proposal' || isPendingPayment) && !groupedDisplays) {
          updateListResolutionsForEditingTolerance();
        } else {
          updateListResolutions(type);
        }
      }
    }
  }, [listOfContentsInCampaign]);

  useEffect(() => {
    if (deletingContentFromLibrary) updateListResolutions('deletingContentFromLibrary');
    defineListResolution(listResolutionWithContent);
    setDeletingContentFromLibrary(false);
  }, [deletingContentFromLibrary]);

  useEffect(() => {
    if (editCampaignActive && listResolutionWithContent.length === 0) return;
    if (!deletingContentFromLibrary) {
      if (groupedDisplays && listResolution) {
        listResolution.forEach(relation => {
          listResolutionWithContent.forEach(rwc => {
            if (
              relation.resolution_height === rwc.resolution_height &&
              relation.resolution_width === rwc.resolution_width &&
              relation.contents.length !== rwc.contents.length
            ) {
              defineListResolution(listResolutionWithContent);
            }
          });
        });
      } else {
        defineListResolution(listResolutionWithContent);
      }
    }

    if (editCampaignActive && !isCreatedEditCampaign) {
      setIsCreatedEditCampaign(true);
      if (!isPaymentStatusApproved) {
        updateListResolutionsForEditingTolerance();
      }
    }
    if (isLoaderView) setIsLoaderView(false);
  }, [listResolutionWithContent]);

  useEffect(() => {
    if (editCampaignActive && !hasInitialBackup && listOfContentsInCampaign.length > 0) {
      setInitialBackup({
        listOfContentsInCampaign: [...listOfContentsInCampaign],
        listResolutionWithContent: [...listResolutionWithContent],
        customContents: [...customContents],
        cart: [...cart]
      });
    }
  }, [editCampaignActive, listOfContentsInCampaign, hasInitialBackup]);

  useEffect(() => {
    if (
      !editCampaignActive &&
      creationType === 'makeProposal' &&
      listResolution &&
      listResolution.length > 0 &&
      placeholders.length === 0
    ) {
      getPlaceholders(
        listResolution.map(r => ({ width: r.resolution_width, height: r.resolution_height }))
      );
    }
  }, [creationType, listResolution, placeholders]);

  useEffect(() => {
    if (
      editCampaignActive &&
      currentCampaign.status === 'proposal' &&
      listResolutionWithContent.length > 0 &&
      placeholders.length === 0
    ) {
      const withoutContent = listResolutionWithContent.filter(r => r.contents.length === 0);

      if (withoutContent.length > 0) {
        getPlaceholders(
          withoutContent.map(r => ({ width: r.resolution_width, height: r.resolution_height }))
        );
      }
    }
  }, [editCampaignActive, currentCampaign && currentCampaign.status, listResolutionWithContent]);

  useEffect(() => {
    if (!editCampaignActive && creationType === 'makeProposal' && placeholders.length > 0) {
      const contentsAsPlaceholders = placeholders.map(ph => ({
        id: 'placeholder' + ph.width + 'x' + ph.height,
        width: ph.width,
        height: ph.height,
        file: ph.base64Image,
        file_thumb: ph.base64Image,
        ready: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: 'image',
        name: null,
        length: null,
        properties: null,
        company_id: null,
        created_by: null,
        updated_by: null,
        archived: 0,
        is_filling: 0,
        origin: 'dsp',
        belong_to_smart: 0,
        belong_to_id: null,
        smartv2_continuous: 0,
        canva_id: null,
        external_id: null,
        content_category_id: null,
        creative_id: null,
        version_name: null,
        all_thumbs: [],
        category: null,
        tags: [],
        count_displays: 0,
        placeholder_size: `${ph.width}x${ph.height}`,
        content_version_name: 'V1'
      }));
      setListOfPlaceholdersInCampaign(contentsAsPlaceholders);

      const withPlaceholders = listResolutionWithContent.map(item => {
        const filtered = contentsAsPlaceholders.filter(
          c => c.width === item.resolution_width && c.height === item.resolution_height
        );

        return {
          ...item,
          contents: filtered,
          contentsDisplays: item.displays.map(d => ({
            contentId: filtered[0].id,
            displayId: d.id
          }))
        };
      });
      setListResolutionWithPlaceholders(withPlaceholders);
    }
  }, [creationType, placeholders]);

  useEffect(() => {
    if (
      editCampaignActive &&
      currentCampaign.status === 'proposal' &&
      listResolutionWithContent.length > 0 &&
      placeholders.length > 0 &&
      !placeholdersInjected.current
    ) {
      const withoutContent = listResolutionWithContent.filter(r => r.contents.length === 0);

      if (withoutContent.length > 0) {
        placeholdersInjected.current = true;
        const contentsAsPlaceholders = placeholders.map(ph => ({
          id: 'placeholder' + ph.width + 'x' + ph.height,
          width: ph.width,
          height: ph.height,
          file: ph.base64Image,
          file_thumb: ph.base64Image,
          ready: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          type: 'image',
          name: 'Placeholder ' + ph.width + 'x' + ph.height,
          length: null,
          origin: 'dsp',
          placeholder_size: `${ph.width}x${ph.height}`,
          content_version_name: 'V1',
          isPlaceholder: true
        }));

        setListOfContentsInCampaign([...listOfContentsInCampaign, ...contentsAsPlaceholders]);

        const updated = listResolutionWithContent.map(item => {
          if (item.contents.length > 0) return item;

          const filtered = contentsAsPlaceholders.filter(
            c => c.width === item.resolution_width && c.height === item.resolution_height
          );

          return {
            ...item,
            contents: filtered,
            contentsDisplays: item.displays.map(d => ({
              contentId: filtered[0].id,
              displayId: d.id
            }))
          };
        });
        setListResolutionWithContent(updated);
      }
    }
  }, [
    editCampaignActive,
    currentCampaign && currentCampaign.status,
    listResolutionWithContent.length,
    placeholders
  ]);

  return {
    sliderRef,
    currentStick,
    setCurrentStick,
    loadingListOfResolutions,
    slidesToScroll,
    listResolution,
    setListResolution,
    hasDifferentResolution,
    hasDifferentDurationSlot,
    isCreatedEditCampaign,
    resolutionFilterIndex,
    setResolutionFilterIndex,
    versionFilter,
    setVersionFilter,
    isLoaderView,

    // memos
    resolutionSubtitle,
    resolutionTitle,
    listContentResolutions,
    listOfContentsInCampaignFiltered,

    // functions
    setTolerance,
    updateListResolutions,
    updateListResolutionsForEditingTolerance,
    isTheLastOfTheRelationship,
    haveContentInRelation,
    checkAddInRelation,
    getTooltipAndCompatibility,
    openNotification,
    applyTolerance
  };
};

export default useResolutionLogic;
